import { datadogLogs } from '@datadog/browser-logs';
import { Extension } from '@tiptap/core';

export interface LineHeightOptions {
  types: string[];
  lineHeights: string[];
  defaultLineHeight: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    lineHeight: {
      /**
       * Set the line height
       */
      setLineHeight: (lineHeight: string) => ReturnType;
      /**
       * Unset the line height
       */
      unsetLineHeight: () => ReturnType;
    };
  }
}

/**
 * Helper function to safely execute TipTap commands with error logging
 */
function safeExecuteCommand(
  commandFn: () => boolean,
  errorContext: {
    operation: string;
    type: string;
    additionalData?: Record<string, unknown>;
  },
): boolean {
  try {
    return commandFn();
  } catch (error) {
    // Log unexpected errors - TipTap commands should return false, not throw
    datadogLogs.logger.error(
      `LineHeight ${errorContext.operation} command failed unexpectedly`,
      {
        type: errorContext.type,
        ...errorContext.additionalData,
        error: error instanceof Error ? error.message : String(error),
      },
      error instanceof Error ? error : undefined,
    );
    return false;
  }
}

/**
 * LineHeight extension for TipTap editor
 * Allows controlling line spacing for paragraphs and headings
 */
export const LineHeight = Extension.create<LineHeightOptions>({
  name: 'lineHeight',

  addOptions() {
    return {
      types: ['heading', 'paragraph'],
      lineHeights: ['1', '1.15', '1.5', '2'],
      defaultLineHeight: '1.15',
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: this.options.defaultLineHeight,
            parseHTML: (element) =>
              element.style.lineHeight || this.options.defaultLineHeight,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) {
                return {};
              }

              return {
                style: `line-height: ${attributes.lineHeight}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (lineHeight: string) =>
        ({ chain }) => {
          if (!this.options.lineHeights.includes(lineHeight)) {
            return false;
          }

          // Try to update each type - at least one should succeed for the current selection
          let updated = false;
          this.options.types.forEach((type) => {
            const result = safeExecuteCommand(
              () => chain().updateAttributes(type, { lineHeight }).run(),
              {
                operation: 'setLineHeight',
                type,
                additionalData: { lineHeight },
              },
            );
            if (result) updated = true;
          });

          return updated;
        },
      unsetLineHeight:
        () =>
        ({ chain }) => {
          // Try to reset each type - at least one should succeed for the current selection
          let updated = false;
          this.options.types.forEach((type) => {
            const result = safeExecuteCommand(
              () => chain().resetAttributes(type, 'lineHeight').run(),
              {
                operation: 'unsetLineHeight',
                type,
              },
            );
            if (result) updated = true;
          });

          return updated;
        },
    };
  },
});
